import { createId, templateCopy } from '@seede/renderer/utils/node';
import { copy } from '@seede/renderer/utils';
import { getToken } from './user';
import { strToU8, compressSync } from 'fflate';
import { md5 } from 'super-fast-md5';

export async function getUserToken() {
  if (window.api) {
    const user = await window.api.getUserToken();
    return user?.token;
  } else {
    return getToken();
  }
}

const isDev = process.env.NODE_ENV === 'development';

export function getAPI(env) {
  if (window.api) {
    return window.api.getAPI();
  } else {
    if (isDev) {
      return `${window.location.protocol}//${window.location.hostname}:3000`;
    }
    const urlParams = new URLSearchParams(window.location.search);
    env = urlParams.get('env') || env || '';
    if (env === 'global') return 'https://api.seede.ai';
    if (env === 'edge') return 'https://api.seedeai.com';
    return 'https://api.seede.ai';
  }
}

export class APIError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
    this.message = message;
  }
}

export async function request(
  url,
  options = {},
  { type = 'json', maxRetries = 1, retryDelay = 1000 } = {},
) {
  const method = options.method || 'GET';

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const headers = options.headers;
      const token = await getUserToken();
      const api = await getAPI();
      let requestUrl = '';

      if (url.startsWith('http')) {
        requestUrl = url;
      } else {
        requestUrl = `${api}${url}`;
      }

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        requestUrl += `?${queryString}`;
      }
      const res = await fetch(requestUrl, {
        ...options,
        headers: {
          'content-type': 'application/json',
          ...headers,
          authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        if (errorData.message) {
          const err = new APIError(
            res.status,
            errorData.message || `HTTP error! status: ${res.status} ${method} ${url}`,
          );
          err.data = errorData;
          err.code = errorData.code;

          // Don't retry for client errors (4xx) except 429 (rate limit)
          if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            throw err;
          }

          // Retry for server errors (5xx) and rate limits (429)
          if (attempt < maxRetries && (res.status >= 500 || res.status === 429)) {
            const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
            console.warn(
              `Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          throw err;
        }

        const err = new APIError(
          res.status,
          errorData.error || `HTTP error! status: ${res.status} ${method} ${url}`,
        );

        // Don't retry for client errors (4xx) except 429 (rate limit)
        if (res.status >= 400 && res.status < 500 && res.status !== 429) {
          throw err;
        }

        // Retry for server errors (5xx) and rate limits (429)
        if (attempt < maxRetries && (res.status >= 500 || res.status === 429)) {
          const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
          console.warn(
            `Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        throw err;
      }

      if (type === 'json') {
        const data = await res.json();
        return data;
      } else if (type === 'blob') {
        const data = await res.blob();
        return data;
      } else {
        const data = await res.text();
        return data;
      }
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      // Retry for network errors
      if (attempt < maxRetries && (error.name === 'TypeError' || error.message.includes('fetch'))) {
        const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
        console.warn(
          `Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`,
          error.message,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      console.error('Request failed:', error);
      throw new Error(`Request failed: ${error.message} ${method} ${url}`);
    }
  }
}

export async function requestStream(url, options = {}, { onMessage, onError, onComplete } = {}) {
  try {
    const headers = options.headers;
    const token = await getUserToken();
    const api = await getAPI();
    const res = await fetch(`${api}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...headers,
        authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const errorData = await res.json();
      const errorMessage =
        errorData.message || errorData.error || `HTTP error! status: ${res.status}`;
      if (onError) {
        onError(new Error(errorMessage));
      }
      throw new Error(errorMessage);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            if (onComplete) {
              onComplete();
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          // Process complete SSE messages
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep the last incomplete chunk in buffer
          for (const line of lines) {
            if (line.trim() && line.startsWith('data:')) {
              const data = line.substring(5).trim();
              if (data !== '[DONE]' && onMessage) {
                onMessage(JSON.parse(data));
              }
            }
          }
        }
      } catch (error) {
        if (onError) {
          onError(error);
        }
        throw error;
      }
    };

    // Start processing the stream
    processStream();

    // Return an object that allows aborting the stream
    return {
      abort: () => {
        reader.cancel();
        if (onComplete) {
          onComplete();
        }
      },
    };
  } catch (error) {
    console.error('Stream request failed:', error);
    if (onError) {
      onError(error);
    }
    throw new Error(`Stream request failed: ${error.message}`);
  }
}

export async function addProject(project) {
  const result = await request('/project', {
    method: 'POST',
    body: JSON.stringify({
      ...project,
      data: btoa(unescape(encodeURIComponent(JSON.stringify(project.data)))),
    }),
  });
  return result;
}

export async function putProject(id, payload) {
  const { data, thumbnail } = payload;
  if (data) {
    delete payload.data;
  }
  if (thumbnail) {
    payload.thumbnailData = thumbnail;
  }

  const result = await request(`/project/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result;
}

export async function updateProjectThumbnail(id, context) {
  let thumbnail = '';
  if (context.api.exportFrameIsReady()) {
    const frame = context.api.getFrames()[0];
    if (!frame) return;

    // Smart DPR optimization based on frame dimensions
    let dpr = 1;
    const frameArea = frame.w * frame.h;
    const maxDimension = Math.max(frame.w, frame.h);

    // Calculate optimal DPR based on frame size and area
    if (frameArea > 4000000) {
      // Very large frames (>4MP)
      dpr = 0.3;
    } else if (frameArea > 2000000) {
      // Large frames (>2MP)
      dpr = 0.4;
    } else if (maxDimension > 2000) {
      // High resolution in one dimension
      dpr = 0.5;
    } else if (maxDimension > 1440) {
      // Medium-large frames
      dpr = 0.6;
    } else if (maxDimension > 800) {
      // Medium frames
      dpr = 0.8;
    } else {
      dpr = 1; // Small frames can use full resolution
    }

    // Ensure minimum quality for very small frames
    if (maxDimension < 400) {
      dpr = Math.min(dpr, 2); // Allow upscaling for small frames
    }

    thumbnail = await context.api.exportImage({
      contentType: 'image/webp',
      dpr: dpr,
      quality: 0.4,
      fast: true,
    });
  }

  // Calculate hash of thumbnail to avoid redundant updates
  // if (thumbnail) {
  //   const thumbnailHash = md5(thumbnail);
  //   if (thumbnailHash === lastThumbnailHash) {
  //     // Thumbnail hasn't changed, skip update
  //     return;
  //   }
  //   lastThumbnailHash = thumbnailHash;
  // }

  if (!thumbnail) {
    return;
  }

  // Convert data URL to Blob for multipart/form-data upload
  const response = await fetch(thumbnail);
  const blob = await response.blob();

  // Create FormData for file upload
  const formData = new FormData();
  formData.append('thumbnail', blob, 'thumbnail.webp');

  const token = await getUserToken();
  const api = await getAPI();
  const requestUrl = `${api}/project/${id}/thumbnail`;

  const res = await fetch(requestUrl, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new APIError(res.status, errorData.message || 'Failed to update thumbnail');
  }

  const result = await res.json();
  return result;
}

export async function updateProjectByData(id, payload) {
  const result = await request(`/project/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result;
}

export async function createSnapshotData(context, frame) {
  const [card, thumbnail] = await Promise.all([
    context.api.exportState({ dropMessages: true }),
    context.api.exportImage({
      frame: frame,
      contentType: 'image/webp',
      dpr: 1,
      quality: 0.4,
    }),
  ]);

  const originalContent = JSON.stringify(card);
  const buf = strToU8(originalContent);
  const compressed = compressSync(buf, { level: 6, mem: 8 });
  const compressedContent = btoa(String.fromCharCode(...compressed));

  const snapshot = {
    thumbnail: thumbnail,
    data: {
      type: 'gz',
      content: compressedContent,
    },
  };

  return snapshot;
}

async function prepareProjectPayload(context) {
  const state = context.providers.state;
  if (state.disposed) return null;
  const data = templateCopy({
    ...context.card,
    props: {
      ...state.state,
    },
  });

  const currentHash = md5(JSON.stringify(data));

  const payload = {
    hash: currentHash,
    timestamp: Date.now(),
    attemptId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };

  // Get frame name with fallback handling
  try {
    const frames = context.api.getFrames();
    const firstFrame = frames && frames.length > 0 ? frames[0] : null;
    const name = firstFrame?.name;
    if (name) {
      payload.meta = {
        name,
      };
    }
  } catch (frameError) {
    console.warn('Failed to get frame name:', frameError);
  }

  if (data) {
    try {
      const originalContent = JSON.stringify(data);
      const buf = strToU8(originalContent);
      const compressed = compressSync(buf, { level: 6, mem: 8 });
      const compressedContent = await bufferToBase64(compressed);

      const originalSize = originalContent.length;
      const compressedSize = compressedContent.length;
      const compressionRatio = (((originalSize - compressedSize) / originalSize) * 100).toFixed(2);

      if (context.debug) {
        console.warn(
          `Compression ratio: ${compressionRatio}% (${originalSize} -> ${compressedSize} bytes)`,
        );
      }

      payload.data = {
        type: 'gz',
        content: compressedContent,
      };
    } catch (compressionError) {
      console.warn('Compression failed, falling back to uncompressed data:', compressionError);
      const originalContent = JSON.stringify(data);
      const base64Content = btoa(unescape(encodeURIComponent(originalContent)));
      payload.data = base64Content;
    }
  }

  return payload;
}

async function bufferToBase64(buffer) {
  // use a FileReader to generate a base64 data URI:
  const base64url = await new Promise((r) => {
    const reader = new FileReader();
    reader.onload = () => r(reader.result);
    reader.readAsDataURL(new Blob([buffer]));
  });
  // remove the `data:...;base64,` part from the start
  return base64url.slice(base64url.indexOf(',') + 1);
}

async function executeProjectUpdate(id, payload, attempt, timeout) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const result = await request(`/project/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal: controller.signal,
      headers: {
        'X-Attempt': attempt.toString(),
        'X-Attempt-Id': payload.attemptId,
      },
    });

    clearTimeout(timeoutId);

    if (result) {
      return result;
    }

    throw new Error(`Update failed: ${result?.error || 'Unknown error'}`);
  } catch (requestError) {
    clearTimeout(timeoutId);
    throw requestError;
  }
}

function isRetryableError(error) {
  return (
    error.name === 'AbortError' ||
    error.name === 'TimeoutError' ||
    error.message?.includes('network') ||
    error.message?.includes('timeout') ||
    error.message?.includes('fetch') ||
    (error.status && error.status >= 500)
  );
}

function calculateRetryDelay(attempt) {
  const baseDelay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
  const jitter = Math.random() * 1000;
  return baseDelay + jitter;
}

// Store last submitted hash for each project
const projectHashCache = new Map();

export async function updateProject(id, context, options = {}) {
  const { retries = 3, timeout = 60000 } = options;
  let lastError = null;

  const payload = await prepareProjectPayload(context);
  if (context.disposed) {
    console.warn('Project update skipped: context disposed');
    return;
  }

  // Check if update is needed by comparing with last submitted hash
  // const lastHash = projectHashCache.get(id);
  // if (lastHash === payload.hash) {
  //   if (context.debug) {
  //     console.info('Project update skipped: no changes detected (MD5 match)');
  //   }
  //   return { skipped: true, reason: 'no_changes' };
  // }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const result = await executeProjectUpdate(id, payload, attempt, timeout);
      if (context.debug && attempt > 1) {
        console.warn(`Project update succeeded on attempt ${attempt}`);
      }

      // Store the hash after successful update
      projectHashCache.set(id, payload.hash);

      return result;
    } catch (error) {
      lastError = error;

      if (context.debug) {
        console.warn(`Project update attempt ${attempt} failed:`, error);
      }

      if (attempt === retries || !isRetryableError(error)) {
        break;
      }

      const delay = calculateRetryDelay(attempt);

      if (context.debug) {
        console.warn(`Retrying in ${Math.round(delay)}ms... (attempt ${attempt + 1}/${retries})`);
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  const errorMessage = `Failed to update project after ${retries} attempts. Last error: ${lastError?.message || 'Unknown error'}`;
  console.error(errorMessage);

  throw new Error(errorMessage);
}

// Helper function to clear hash cache for a project (useful when needed)
export function clearProjectHashCache(id) {
  if (id) {
    projectHashCache.delete(id);
  } else {
    projectHashCache.clear();
  }
}

export async function removeProject(id, { confirmed = false } = {}) {
  const url = confirmed ? `/project/${id}/confirmed` : `/project/${id}`;
  const result = await request(url, {
    method: 'DELETE',
  });
  return result;
}

async function getProjectDataByJSON(url) {
  try {
    const data = await fetch(url).then((res) => res.json());
    return data;
  } catch {
    const fallback = url.replace('https://assets.seede.ai/', 'https://static.seedeai.com/');
    const data = await fetch(fallback).then((res) => res.json());
    return data;
  }
}

async function getProjectData(url) {
  const fileExtension = url.split('.').pop().split('?')[0]; // Extract extension before query params

  switch (fileExtension.toLowerCase()) {
    case 'json':
      return await getProjectDataByJSON(url);
    default:
      // Default to JSON if extension is unknown
      return await getProjectDataByJSON(url);
  }
}

export async function getProject(id, accessPassword) {
  const query = {};
  if (accessPassword) {
    query.password = accessPassword;
  }
  const project = await request(`/project/${id}`, { query });
  const data = await getProjectData(project.content);
  project.data = data;
  return project;
}

export async function getTaskStatus(id) {
  return request(`/api/task/${id}/status`);
}

export async function claimDailyCredits() {
  return request('/credits/daily', {
    method: 'POST',
  });
}

export async function getDailyCreditsStatus() {
  return request('/credits/daily/status');
}

export async function getTemplate(id) {
  const result = await request(`/template/${id}`, {
    method: 'GET',
  });
  return result;
}

export const emptyProject = {
  type: 'seedeGraphic',
  props: {
    input: {},
    nodes: {
      xd8es1lfb: {
        id: 'xd8es1lfb',
        type: 'frame',
        name: '',
        x: 0,
        y: 0,
        fill: '#ffffff',
        w: 1200,
        h: 628,
        index: 'a0',
      },
      '7k8b0a5n7': {
        rotation: 0,
        type: 'text',
        text: 'Hello Seede',
        fontSize: 90,
        fill: 'linear-gradient(to bottom right, #ff6348, #ff9ff3, #3742fa)',
        height: 'Hug',
        x: 263,
        y: 659,
        w: 554,
        h: 122,
        parentId: 'xd8es1lfb',
        id: '7k8b0a5n7',
        name: 'Text',
        index: 'a1',
        highlights: [
          {
            selection: [6, 11],
            type: 'mark',
            h: 0.5,
            fill: 'linear-gradient(to bottom right, #ff7675, #74b9ff)',
            id: '0',
          },
        ],
        fontFamily: 'Poppins',
        styles: null,
        fontWeight: 'bold',
        shadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
      },
    },
  },
};

export function createEmptyProject({ w = 1080, h = 1920 } = {}) {
  const newProject = copy(emptyProject);
  const oldId = 'xd8es1lfb';
  const frame = newProject.props.nodes[oldId];
  delete newProject.props.nodes[oldId];
  const newId = createId();
  frame.id = newId;
  frame.w = w || frame.w;
  frame.h = h || frame.h;
  newProject.props.nodes[frame.id] = frame;
  Object.values(newProject.props.nodes).map((node) => {
    if (node.parentId === oldId) node.parentId = newId;
  });
  return newProject;
}
